import { Link, useParams } from "react-router-dom";
import { useList, dayName, mediaUrl } from "@/lib/api";
import { Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { PublicLayout } from "@/pages/public/PublicSite";
import {
  ArrowLeft, ArrowRight, Award, Trophy, CalendarDays, PlayCircle, Star, Quote,
} from "lucide-react";

const initials = (name) => (name || "").split(" ").map((w) => w[0]).slice(0, 2).join("");

export default function PublicTeacherProfile() {
  const { slug } = useParams();
  const { data: p, loading } = useList(`/public/teachers/${slug}`, [slug]);

  if (loading) return <PublicLayout><Spinner full /></PublicLayout>;
  if (!p) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="teacher-profile-missing">
          <EmptyState text="This teacher profile is not available." />
          <Button asChild className="mt-6"><Link to="/teachers">Back to teachers</Link></Button>
        </div>
      </PublicLayout>
    );
  }

  const registerQuery = (() => {
    const params = [];
    if (p.id) params.push(`teacherId=${p.id}`);
    return params.length ? `?${params.join("&")}` : "";
  })();
  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12" data-testid="public-teacher-profile">
        <Link to="/teachers" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8" data-testid="back-to-teachers">
          <ArrowLeft className="h-4 w-4" />All Music Instructors
        </Link>

        <div className="flex flex-col md:flex-row gap-8 md:items-start">
          {p.profile_photo_url ? (
            <img src={mediaUrl(p.profile_photo_url)} alt={p.full_name}
              className="h-36 w-36 rounded-lg object-cover border shrink-0" data-testid="profile-photo" />
          ) : (
            <div className="h-36 w-36 rounded-lg bg-accent flex items-center justify-center font-display font-bold text-4xl text-primary shrink-0" data-testid="profile-initials">
              {initials(p.full_name)}
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-4xl sm:text-5xl font-bold" data-testid="profile-name">{p.full_name}</h1>
            <div className="flex flex-wrap gap-2 mt-3">
              {p.years_experience != null && (
                <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-secondary/10 text-secondary">
                  {p.years_experience}+ years experience
                </span>
              )}
              {(p.courses || []).map((c) => (
                <span key={c} className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-muted text-muted-foreground">{c}</span>
              ))}
            </div>
            <p className="text-muted-foreground mt-4 max-w-2xl leading-relaxed" data-testid="profile-bio">
              {p.long_bio || p.short_bio}
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              {p.booking_enabled && (
                <Button asChild size="lg" className="bg-secondary hover:bg-[#D96C52]" data-testid="profile-register-button">
                  <Link to={`/register${registerQuery}`}>
                    Register with this Music Instructor <ArrowRight className="h-4 w-4 ml-1" />
                  </Link>
                </Button>
              )}
              <Button asChild size="lg" variant="outline" data-testid="profile-enquiry-button">
                <Link to="/enquiry" state={{ course_interest: p.courses?.[0], preferred_teacher: p.full_name }}>Ask a question</Link>
              </Button>
              {p.intro_video_url && (
                <Button asChild size="lg" variant="ghost" data-testid="profile-video-button">
                  <a href={p.intro_video_url} target="_blank" rel="noreferrer">
                    <PlayCircle className="h-4 w-4 mr-1.5" />Watch intro video
                  </a>
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-14">
          {(p.qualifications || []).length > 0 && (
            <div className="rounded-lg border bg-card p-7" data-testid="profile-qualifications">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Award className="h-5 w-5 text-secondary" />Qualifications
              </h3>
              <ul className="space-y-2.5">
                {p.qualifications.map((q, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-secondary mt-1.5 shrink-0" />{q}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(p.achievements || []).length > 0 && (
            <div className="rounded-lg border bg-card p-7" data-testid="profile-achievements">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Trophy className="h-5 w-5 text-secondary" />Achievements
              </h3>
              <ul className="space-y-2.5">
                {p.achievements.map((a, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-secondary mt-1.5 shrink-0" />{a}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {p.exam_highlights && (p.exam_highlights.exam_passes > 0 || p.exam_highlights.distinctions > 0) && (
            <div className="rounded-lg border bg-card p-7" data-testid="profile-exam-highlights">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Star className="h-5 w-5 text-secondary" />Student exam results
              </h3>
              <p className="text-3xl font-bold font-display">{p.exam_highlights.exam_passes}</p>
              <p className="text-sm text-muted-foreground">exam passes in the last 12 months
                {p.exam_highlights.distinctions > 0 && ` — including ${p.exam_highlights.distinctions} with distinction`}</p>
            </div>
          )}
          {(p.availability || []).length > 0 ? (
            <div className="rounded-lg border bg-card p-7" data-testid="profile-availability">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-secondary" />Available days &amp; times
              </h3>
              <div className="space-y-2">
                {p.availability.map((a, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="font-medium">{dayName(a.day_of_week)}</span>
                    <span className="text-muted-foreground font-mono text-xs">{a.start_time} – {a.end_time}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border bg-card p-7" data-testid="profile-availability-fallback">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-secondary" />Available days &amp; times
              </h3>
              <p className="text-sm text-muted-foreground">
                Available schedule will be confirmed by admin after registration.
              </p>
            </div>
          )}
        </div>

        {(p.testimonials || []).length > 0 && (
          <section className="mt-14" data-testid="profile-testimonials">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6">What parents say</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 stagger-in">
              {p.testimonials.map((t, i) => (
                <div key={i} className="rounded-lg border bg-card p-7 lift" data-testid={`testimonial-card-${i}`}>
                  <Quote className="h-6 w-6 text-secondary/40 mb-3" />
                  <p className="text-sm leading-relaxed">{t.testimonial_text}</p>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t">
                    <p className="text-xs font-semibold">{t.display_name}</p>
                    {t.rating != null && (
                      <span className="text-xs font-bold text-[#B8860B]">{"★".repeat(Math.round(t.rating))}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </PublicLayout>
  );
}
